import {ReactElement, useEffect, useState} from "react";
import {Field, Input, Label, Message} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import {Col, Row} from "@zendeskgarden/react-grid";
import {
    PayrollCompensationItemCalculationType,
    PayrollCompensationItemModel,
    PayrollCompensationItemType,
} from "../../../../../__generated__/generated_types";
import styles from "../PayrollDefaults.module.scss";
import {LocalCompItemDefault} from "../DefaultsForm";
import PayrollDatePicker from "../../../../../components/analyst-payroll-drawer/components/PayrollDatePicker";
import {ensureDateInUtc} from "../../../../../components/analyst-payroll-drawer/date-helpers";
import {v4 as uuid} from "uuid";

interface DefaultsTaxesAndInsuranceProps {
    compItemDefaults: LocalCompItemDefault[];
    setCompItemDefaults: (items: LocalCompItemDefault[]) => void;
    compensationItems: Pick<PayrollCompensationItemModel, "id" | "type" | "customName">[];
    onResetType: (type: PayrollCompensationItemType) => void;
    isTypeChanged: (type: PayrollCompensationItemType) => boolean;
    onValidationChange?: (hasErrors: boolean) => void;
    showResetControls?: boolean;
}

type ValidationErrors = Record<string, string>;
type TouchedFields = Record<string, boolean>;

const percentOnlyTaxTypes = [
    PayrollCompensationItemType.MedicareTax,
    PayrollCompensationItemType.SocialSecurityTax,
    PayrollCompensationItemType.FederalUnemploymentTax,
    PayrollCompensationItemType.StateUnemploymentTax,
    PayrollCompensationItemType.WorkersCompensation,
];

function hasValue(value: string | undefined | null): boolean {
    return (value ?? "").trim().length > 0;
}

export default function DefaultsTaxesAndInsurance(props: DefaultsTaxesAndInsuranceProps): ReactElement {
    const {
        compItemDefaults,
        setCompItemDefaults,
        compensationItems,
        onResetType,
        isTypeChanged,
        onValidationChange,
        showResetControls = true,
    } = props;
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [touchedFields, setTouchedFields] = useState<TouchedFields>({});

    function markTouched(fieldKey: string) {
        setTouchedFields(current => ({...current, [fieldKey]: true}));
    }

    function fieldError(fieldKey: string): string | undefined {
        return touchedFields[fieldKey] ? errors[fieldKey] : undefined;
    }

    const taxGroups = [{
        label: "Medicare",
        type: PayrollCompensationItemType.MedicareTax,
    }, {
        label: "Social Security Tax",
        type: PayrollCompensationItemType.SocialSecurityTax,
    }, {
        label: "Federal Unemployment Tax",
        type: PayrollCompensationItemType.FederalUnemploymentTax,
    }, {
        label: "State Unemployment Tax",
        type: PayrollCompensationItemType.StateUnemploymentTax,
    }, {
        label: "Workers' Compensation",
        type: PayrollCompensationItemType.WorkersCompensation,
    }];

    useEffect(() => {
        const hasInvalidCalculationType = compItemDefaults.some(item =>
            percentOnlyTaxTypes.includes(item.compensationItemType) &&
            item.calculationType !== PayrollCompensationItemCalculationType.PercentOfPrimaryCompensation
        );

        if(!hasInvalidCalculationType) {
            return;
        }

        setCompItemDefaults(compItemDefaults.map(item => {
            if(!percentOnlyTaxTypes.includes(item.compensationItemType)) {
                return item;
            }

            return {
                ...item,
                calculationType: PayrollCompensationItemCalculationType.PercentOfPrimaryCompensation,
            };
        }));
    }, [compItemDefaults, setCompItemDefaults]);

    useEffect(() => {
        const nextErrors: ValidationErrors = {};

        compItemDefaults.forEach(item => {
            if(!hasValue(item.costPerPayPeriod)) nextErrors[`${item._localId}.costPerPayPeriod`] = "% of Salary is required";
            if(!item.effectiveStartDate) nextErrors[`${item._localId}.effectiveStartDate`] = "Effective Date is required";
        });

        setErrors(nextErrors);
        onValidationChange?.(Object.keys(nextErrors).length > 0);
    }, [compItemDefaults, onValidationChange]);

    function getItemsForType(type: PayrollCompensationItemType): LocalCompItemDefault[] {
        return compItemDefaults.filter(item => item.compensationItemType === type);
    }

    function updateItem(localId: string, updates: Partial<LocalCompItemDefault>) {
        setCompItemDefaults(compItemDefaults.map(item =>
            item._localId === localId ? {...item, ...updates} : item
        ));
    }

    function removeItem(localId: string) {
        setCompItemDefaults(compItemDefaults.filter(item => item._localId !== localId));
    }

    function addItem(type: PayrollCompensationItemType) {
        const compItem = compensationItems.find(ci => ci.type === type);
        if(!compItem) return;
        setCompItemDefaults([...compItemDefaults, {
            _localId: uuid(),
            compensationItemId: compItem.id,
            compensationItemType: type,
            calculationType: PayrollCompensationItemCalculationType.PercentOfPrimaryCompensation,
            costPerPayPeriod: "",
            annualCostCap: "",
            effectiveStartDate: undefined,
        }]);
    }

    return (
        <div className={styles.subForm}>
            {taxGroups.map(group => {
                const items = getItemsForType(group.type);
                return (
                    <div className={styles.section} key={group.label}>
                            <Row justifyContent="between">
                                <Col size={8}><h4 className={styles.sectionHeader}>{group.label}</h4></Col>
                                {showResetControls && <Col textAlign="end"><Button isBasic disabled={!isTypeChanged(group.type)} onClick={() => onResetType(group.type)}>Undo</Button></Col>}
                            </Row>
                            {items.map((item, index) => {
                                return (
                                    <div key={item._localId}>
                                        <Field className={styles.field}>
                                            <Row justifyContent="between">
                                                <Col>
                                                    <Label className={styles.subHeader}>Payment {index + 1}</Label>
                                                </Col>
                                                <Button isBasic isDanger onClick={() => removeItem(item._localId)}>Remove</Button>
                                            </Row>
                                            <Row justifyContent="between">
                                                <Col>
                                                    <Field className={styles.field}>
                                                        <Label className={styles.label}>% of Salary</Label>
                                                        <Input
                                                            placeholder="%"
                                                            value={item.costPerPayPeriod}
                                                            type="number"
                                                            validation={fieldError(`${item._localId}.costPerPayPeriod`) ? "error" : undefined}
                                                            onBlur={() => markTouched(`${item._localId}.costPerPayPeriod`)}
                                                            onChange={(evt) => updateItem(item._localId, {costPerPayPeriod: evt.target.value})}
                                                        />
                                                        {fieldError(`${item._localId}.costPerPayPeriod`) && <Message validation="error">{fieldError(`${item._localId}.costPerPayPeriod`)}</Message>}
                                                    </Field>
                                                </Col>
                                                <Col>
                                                    <Field className={styles.field}>
                                                        <Label className={styles.label}>Compensation Cap</Label>
                                                        <Input
                                                            placeholder="$ up to"
                                                            value={item.annualCostCap}
                                                            type="number"
                                                            onChange={(evt) => updateItem(item._localId, {annualCostCap: evt.target.value})}
                                                        />
                                                    </Field>
                                                </Col>
                                            </Row>
                                            <Row justifyContent="between">
                                                <Col size={12}>
                                                    <Field className={styles.field}>
                                                        <Label className={styles.label}>Effective Date</Label>
                                                        <PayrollDatePicker
                                                            dateValue={item.effectiveStartDate ?? ""}
                                                            onDateChange={(newDate) => {
                                                                updateItem(item._localId, {effectiveStartDate: ensureDateInUtc(newDate)?.toISOString()});
                                                            }}
                                                            onBlur={() => markTouched(`${item._localId}.effectiveStartDate`)}
                                                        />
                                                        {fieldError(`${item._localId}.effectiveStartDate`) && <Message validation="error">{fieldError(`${item._localId}.effectiveStartDate`)}</Message>}
                                                    </Field>
                                                </Col>
                                            </Row>
                                        </Field>
                                    </div>
                                );
                            })}
                            <Button isBasic className={styles.addButton} onClick={() => addItem(group.type)}>Add</Button>
                    </div>
                );
            })}
        </div>
    );
}
